<template>
  <div>
    <!-- Text-and-Button:start -->
    <div class="flex items-baseline justify-between mt-10 relative">
      <h2 class="intro-y text-lg font-medium -top-1">
        {{ $t(`${viewRoot}.list`) }}
      </h2>
      <button type="button" class="btn btn-primary w-40 md:w-34 h-8" @click="newAlert">
        <span>{{ $t(`${viewRoot}.new`) }}</span>
      </button>
    </div>
    <!-- Text-and-Button:end -->

    <!-- Main-container:start -->
    <div class="grid grid-cols-12 gap-6 mb-12">
      <!--Header:start-->
      <div class="intro-y col-span-12 flex flex-wrap sm:flex-nowrap items-center mt-2">
        <p class="mr-auto">{{ $t(`${viewRoot}.description`) }}</p>
      </div>
      <!--Header:end-->

      <!-- Filters:start -->
      <div class="intro-y col-span-12 flex flex-wrap sm:flex-nowrap items-center mt-2">
        <div class="w-full grid sm:grid-cols-2 lg:grid-cols-6 xl:grid-cols-4 gap-6 mt-3 sm:mt-0 sm:ml-auto md:ml-0">
          <!--Campo buscador-->
          <div class="w-full sm:w-auto mt-3 sm:mt-0 sm:ml-auto md:ml-0">
            <div class="w-full relative lg:col-span-2 xl:col-span-1 text-gray-700 dark:text-gray-300">
              <input
                type="text"
                class="form-control w-full box pr-10 placeholder-theme-13"
                :placeholder="$t(`${viewRoot}.search_text`)"
                v-model="searchInput"
                @keyup="filterAlerts"
              />
              <SearchIcon class="w-4 h-4 absolute my-auto inset-y-0 mr-3 right-0" />
            </div>
          </div>

          <!---Biological Sex-->
          <div class="w-full relative lg:col-span-2 xl:col-span-1 text-gray-700 dark:text-gray-300">
            <TomSelect class="w-full cursor" v-model="biological_sex" @change="filterAlerts">
              <option value="0">{{ $t(`${viewRoot}.form.biological_sex.placeholder`) }}</option>
              <option
                v-for="(key, idx) in biologicalSexKeys"
                :key="`biological_sex-${idx}`"
                :value="key"
                :label="`biological_sex-${idx}`"
              >
                {{ $t(`globals.biological_sex.${key}`) }}
              </option>
            </TomSelect>
          </div>

          <!---Action type-->
          <div class="w-full relative lg:col-span-2 xl:col-span-1 text-gray-700 dark:text-gray-300">
            <TomSelect class="w-full cursor" v-model="action_type" @change="filterAlerts">
              <option value="0">{{ $t(`${viewRoot}.form.action_type.placeholder`) }}</option>
              <option
                v-for="(key, idx) in actionTypeKeys"
                :key="`action-type-${idx}`"
                :value="key"
                :label="`action_type-${idx}`"
              >
                {{ $t(`globals.action_type.${key}`) }}
              </option>
            </TomSelect>
          </div>

          <!--STATUS-->
          <div class="w-full relative text-gray-700 dark:text-gray-300">
            <TomSelect class="w-full cursor" v-model="status" @change="filterAlerts">
              <option value="0">{{ $t(`${viewRoot}.form.status.placeholder`) }}</option>
              <!-- Texto para mostrar 'Todos' los estados -->
              <option value="PENDING_APPROVAL">{{ $t(`${viewRoot}.to_be_approved`) }}</option>
              <option value="REJECTED">{{ $t(`${viewRoot}.disapproved`) }}</option>
              <option value="APPROVED">{{ $t(`${viewRoot}.approved`) }}</option>
            </TomSelect>
          </div>
        </div>

        <button @click="filterClean" type="button" class="btn btn-custom-gray-1 w-24">
          {{ $t("views.clean") }}
        </button>
      </div>
      <!-- Filters:end -->

      <!-- Data-List:start -->
      <div class="intro-y col-span-12 overflow-auto">
        <table class="table table-report -mt-2 overflow-hidden">
          <thead>
            <tr>
              <th class="whitespace-nowrap">
                <div class="grid alerts__grid">
                  <div class="title__table">ID</div>
                  <div class="title__table">{{ $t(`${viewRoot}.table.title1`) }}</div>
                  <div class="title__table">{{ $t(`${viewRoot}.table.title2`) }}</div>
                  <div class="title__table">{{ $t(`${viewRoot}.table.title3`) }}</div>
                  <div class="title__table">{{ $t(`${viewRoot}.table.title4`) }}</div>
                </div>
              </th>
              <th class="text-center whitespace-nowrap">
                {{ $t(`${viewRoot}.table.actions`) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(alert, alertKey) in alerts" :key="alertKey">
              <tr class="intro-x">
                <td>
                  <div class="grid alerts__grid cursor-pointer">
                    <!--ID Alert-->
                    <div>{{ alert.id }}</div>

                    <!--Title-->
                    <div>{{ alert.title }}</div>

                    <!--Biological Sex-->
                    <div>
                      {{ $t(`globals.biological_sex.${alert.biological_sex}`) }}
                    </div>

                    <!--Action Type-->
                    <div>{{ $t(`globals.action_type.${alert.action_type}`) }}</div>

                    <!--Status-->
                    <div
                      class="alert show py-1 px-4 w-max h-max-content font-medium"
                      :class="{
                        'green-ok text-white': alert.status === 'APPROVED',
                        'red-danger text-white': alert.status === 'REJECTED',
                        'orange-medium text-black': alert.status === 'PENDING_APPROVAL',
                      }"
                    >
                      {{ $t(`globals.status_alerts.${alert.status}`) }}
                    </div>
                  </div>
                </td>

                <!-- Section-Actions:start -->
                <td>
                  <div class="flex justify-center items-center">
                    <a class="flex items-center mr-3" href="javascript:;" @click="editAlert(alert.id)">
                      <CheckSquareIcon class="w-4 h-4 mr-1" />
                      {{ $t("views.edit") }}
                    </a>
                  </div>
                </td>
                <!-- Section-Actions:end -->
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <!-- Data-List:start -->
    </div>
    <!-- Main-container:end -->

    <!-- Pagination:start -->
    <PaginationControls
      v-model:page="pagination.page"
      v-model:per_page="pagination.per_page"
      :total_pages="pagination.total_pages"
      :total="pagination.total"
      :sizes="[3, 10, 25, 35, 50]"
      @request="getAlerts"
    >
      <template #summary>
        {{ $t("components.pagination.viewing", { total: alerts.length }) }}
      </template>
    </PaginationControls>
    <!-- Pagination:end -->
  </div>
</template>

<script>
import AlertsService from "@/services/alerts.service";
import PaginationControls from "@/components/pagination-control/Main.vue";

export default {
  name: "AlertsList",
  components: {
    PaginationControls,
  },
  data() {
    return {
      // Data
      alerts: [],
      biological_sex: null,
      action_type: null,
      status: 0,
      // Common
      viewRoot: "views.alerts",
      searchInput: "",
      debounceTimeout: null,
      biologicalSexKeys: this.$global.getGenders(),
      actionTypeKeys: this.$global.getActionTypesAlerts(),
      // Pagination
      pagination: {
        page: 0,
        total_pages: 0,
        per_page: 10,
        total: 0,
      },
    };
  },
  mounted() {
    this.getInitialData();
  },
  watch: {
    "$i18n.locale": function (l) {
      this.getInitialData();
    },
  },
  methods: {
    /**
     * *Initial Data
     */
    async getInitialData() {
      await this.getAlerts();
    },
    async getAlerts() {
      const params = {
        search: this.searchInput || "",
        biological_sex: this.biological_sex,
        action_type: this.action_type,
        status: this.status,
        pagination: this.pagination,
      };
      const { data, pagination } = await AlertsService.getAllAlerts(params, localStorage.lang);
      this.alerts = data;
      this.paginationSetData(pagination);
    },
    filterAlerts() {
      clearTimeout(this.debounceTimeout);

      this.debounceTimeout = setTimeout(() => {
        this.pagination.page = 0;
        this.getAlerts();
      }, 1000);
    },

    newAlert() {
      localStorage.removeItem("questionWithAnswers");
      this.$router.push({ path: "/alerts-new" });
    },

    editAlert(id) {
      this.$router.push({ path: "/alerts-edit", query: { id } });
    },

    // Pagination:start
    paginationSetData({ page = 0, per_page: perPage = 0, total = 0, total_pages: totalPages = 0 } = {}) {
      this.pagination.page = page;
      this.pagination.per_page = perPage;
      this.pagination.total = total;
      this.pagination.total_pages = totalPages;

      console.log(this.pagination);
    },
    // Pagination:end
    filterClean() {
      this.searchInput = "";
      this.biological_sex = null;
      this.action_type = null;
      this.status = 0;
      this.getAlerts();
    },
  },
};
</script>

<style lang="scss" scoped>
.alerts__grid {
  display: grid;
  // Default for smaller screens, adjust as needed
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  align-items: center;
  width: 100%;
  gap: 10px; // Add some gap between items

  @media screen and (min-width: 768px) {
    // Adjust breakpoint for tablets/desktops
    grid-template-columns: 0.5fr 2fr 1.5fr 1.5fr 1.5fr; // Example for 5 columns on wider screens
  }

  @media screen and (min-width: 1400px) {
    grid-template-columns: 0.5fr 3fr 1.5fr 1.5fr 1.5fr; // Further adjustment for very wide screens
  }

  .title__table,
  .content__table {
    text-wrap: wrap;
    overflow: hidden; // Ensure text doesn't overflow
    text-overflow: ellipsis; // Add ellipsis for overflowing text
  }
}
</style>
